/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { isDeveloperEnvironment } from './environment';
import { reportRateLimit } from './reportRateLimit';
import { parseRetryAfterMs } from './retryAfter';

/**
 * The parameter that raises the notice, carrying the wait to count down: a
 * `retry-after` in the URL rather than in a response header, so
 * `?rateLimitNotice=45` counts down forty-five seconds and `?rateLimitNotice=900`,
 * past the five-minute ceiling, falls back to the one-minute default. Present with no
 * value reads as an absent header and counts down the one-minute default too.
 */
const REVIEW_PARAM = 'rateLimitNotice';

/**
 * Raises the notice on any page whose URL asks for it, so QA, designers and the
 * client can review the copy and layout in a deployed non-production environment
 * without a developer in the loop.
 *
 * It reports through the same function a service detecting its own 429s calls, so
 * what a reviewer sees is the notice itself and not a mock of it — the countdown, the
 * announcements, the analytics, the suppression window, the reload. The parameter
 * only stands in for the 429.
 *
 * Read once, on mount: the parameter summons one notice rather than arming the page
 * to keep producing them, so acknowledging it is the end of it. An automatic reload
 * lands back on the same URL and does raise a fresh notice, which is what a bucket
 * that is still limited does anyway — take the parameter out of the URL to stop it. The
 * shortest that loop can run is the thirty-second floor; a value below it falls back to
 * the one-minute default, which is a gap a reviewer can act inside; the README says so
 * where they will read it.
 *
 * Gated on the per-environment variable rather than build mode, because every
 * deployed environment runs a production build and a build-mode check would kill the
 * trigger in exactly the environments it is wanted in. The devtools flag is no use
 * either: it is off in the test environment, which is where review happens. See
 * `./environment`.
 *
 * Internal, and mounted by `RateLimitModal` — this drives the module's own state, so
 * a service rendering `ControlledRateLimitModal` from its own state is not reached by
 * it.
 */
export function useRateLimitReviewTrigger() {
    React.useEffect(() => {
        if (!isDeveloperEnvironment()) {
            return;
        }

        const params = new URLSearchParams(window.location.search);

        if (!params.has(REVIEW_PARAM)) {
            return;
        }

        reportRateLimit(parseRetryAfterMs(params.get(REVIEW_PARAM)));
    }, []);
}
